import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import taxDeclarationService from '../../services/taxDeclarationService';

export const fetchTaxDeclarations = createAsyncThunk(
  'taxDeclaration/fetchTaxDeclarations',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await taxDeclarationService.getAll(filters);
      return response.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tax declarations');
    }
  }
);

export const fetchTaxDeclarationByFY = createAsyncThunk(
  'taxDeclaration/fetchByFY',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await taxDeclarationService.getByFY(filters);
      return response.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tax declaration');
    }
  }
);

export const createTaxDeclaration = createAsyncThunk(
  'taxDeclaration/create',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await taxDeclarationService.create(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create tax declaration');
    }
  }
);

export const updateTaxDeclaration = createAsyncThunk(
  'taxDeclaration/update',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await taxDeclarationService.update(id, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update tax declaration');
    }
  }
);

export const deleteTaxDeclaration = createAsyncThunk(
  'taxDeclaration/delete',
  async ({ id, organizationId }, { rejectWithValue }) => {
    try {
      await taxDeclarationService.delete(id, organizationId);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete tax declaration');
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  error: null,
  selectedDeclaration: null
};

const taxDeclarationSlice = createSlice({
  name: 'taxDeclaration',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedDeclaration: (state, action) => {
      state.selectedDeclaration = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch all
    builder.addCase(fetchTaxDeclarations.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTaxDeclarations.fulfilled, (state, action) => {
      state.loading = false;
      state.list = action.payload;
    });
    builder.addCase(fetchTaxDeclarations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Fetch by FY
    builder.addCase(fetchTaxDeclarationByFY.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTaxDeclarationByFY.fulfilled, (state, action) => {
      state.loading = false;
      state.selectedDeclaration = action.payload;
    });
    builder.addCase(fetchTaxDeclarationByFY.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Create
    builder.addCase(createTaxDeclaration.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createTaxDeclaration.fulfilled, (state, action) => {
      state.loading = false;
      state.list.push(action.payload);
    });
    builder.addCase(createTaxDeclaration.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Update
    builder.addCase(updateTaxDeclaration.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateTaxDeclaration.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.list.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.list[index] = action.payload;
      }
    });
    builder.addCase(updateTaxDeclaration.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Delete
    builder.addCase(deleteTaxDeclaration.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteTaxDeclaration.fulfilled, (state, action) => {
      state.loading = false;
      state.list = state.list.filter((item) => item.id !== action.payload);
    });
    builder.addCase(deleteTaxDeclaration.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  }
});

export const { clearError, setSelectedDeclaration } = taxDeclarationSlice.actions;
export default taxDeclarationSlice.reducer;
